import { cs } from '../../lib/currency';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  CreditCard,
  Database,
  RefreshCw,
  ShieldAlert,
  Wrench,
  XCircle,
  ChevronRight,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import TenantDriftPanel from '../super-admin/TenantDriftPanel';
import { RETIRED_TOOLS_VISIBLE } from '../../lib/retiredTools';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ConfirmDialog,
} from '../../components/ui/confirm-dialog';

const API = (typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL) + '/api';

// Super Admin runs these per tenant via /super-admin/tenants/{id}/payment-ops.
let OPS_TENANT = null;
const opsBase = (legacy) => (OPS_TENANT ? `${API}/super-admin/tenants/${OPS_TENANT}/payment-ops` : `${API}${legacy}`);

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// ---------------------------------------------------------------------------
// Tiny utility components
// ---------------------------------------------------------------------------

const KpiTile = ({ label, value, icon: Icon, tone = 'slate', testid }) => {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
  };
  return (
    <div
      className={`rounded-lg border px-4 py-3 ${tones[tone] || tones.slate}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide opacity-80">{label}</span>
        {Icon && <Icon className="h-4 w-4 opacity-60" />}
      </div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
    {message}
  </div>
);

const SectionPanel = ({ title, description, action, children, testid }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          {description && (
            <p className="mt-1 text-xs text-slate-500">{description}</p>
          )}
        </div>
        {action}
      </div>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

// iter306ac — graceful error renderer. Failures in the Payment-Ops
// endpoints used to surface as a transient toast and a vanishing UI
// (the data state was reset to null). This banner stays in place,
// classifies the failure, and links to the most likely remedy.
const interpretError = (err) => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
  const detailStr = String(detail);
  const lower = detailStr.toLowerCase();
  // Stripe key misconfigured (preview placeholder, rotated key, etc.)
  if (lower.includes('invalid api key') || lower.includes('no such api key')) {
    return {
      severity: 'rose',
      title: 'Stripe API key is invalid',
      body: detailStr,
      action: { to: '/admin/payment-settings', label: 'Open Payment Settings' },
    };
  }
  if (status === 504 || lower.includes('gateway') || lower.includes('timeout')) {
    return {
      severity: 'amber',
      title: 'Request timed out',
      body: 'Try a smaller time window or hit Refresh in a few seconds.',
    };
  }
  if (status === 404) {
    return {
      severity: 'amber',
      title: 'Endpoint not available',
      body:
        'This Payment Operations endpoint may not be deployed yet. ' +
        'If you are on production, redeploy from Emergent to ship the latest backend.',
    };
  }
  if (status === 401 || status === 403) {
    return {
      severity: 'rose',
      title: 'Not authorised',
      body: 'Sign in as an admin to view this data.',
    };
  }
  if (status === 502 || status === 503) {
    return {
      severity: 'rose',
      title: 'Upstream service unavailable',
      body: detailStr,
    };
  }
  return {
    severity: 'rose',
    title: status ? `Request failed (${status})` : 'Request failed',
    body: detailStr,
  };
};

const ErrorBanner = ({ err, onRetry, testid }) => {
  if (!err) return null;
  const info = interpretError(err);
  const tones = {
    rose: 'border-rose-300 bg-rose-50 text-rose-900',
    amber: 'border-amber-300 bg-amber-50 text-amber-900',
  };
  return (
    <div
      className={`rounded-md border px-4 py-3 text-sm ${tones[info.severity] || tones.rose}`}
      data-testid={testid || 'payment-ops-error-banner'}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">{info.title}</div>
            <div className="mt-0.5 text-xs opacity-90">{info.body}</div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {info.action && (
            <Link to={info.action.to}>
              <Button size="sm" variant="outline" data-testid="payment-ops-error-action">
                {info.action.label}
              </Button>
            </Link>
          )}
          {onRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={onRetry}
              data-testid="payment-ops-error-retry"
            >
              <RefreshCw className="mr-1 h-3 w-3" />
              Retry
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// HEALTH tab — Unreconciled Stripe PIs
// ---------------------------------------------------------------------------

const HealthTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${opsBase('/admin/payments')}/unreconciled?days=${days}`,
        { headers: getAuthHeaders() },
      );
      setData(res.data);
    } catch (e) {
      setError(e);
      // Keep `data` in place so the previous successful KPIs remain visible
      // instead of the page going blank on a transient failure.
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-600">Window:</span>
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
          <SelectTrigger className="w-32" data-testid="health-window-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="14">14 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
            <SelectItem value="60">60 days</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          data-testid="health-refresh-btn"
        >
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <ErrorBanner err={error} onRetry={fetchData} testid="health-error-banner" />

      {data && (
        <div className="grid gap-3 md:grid-cols-4">
          <KpiTile
            label="PIs scanned"
            value={data.scanned ?? 0}
            icon={Activity}
            testid="kpi-scanned"
          />
          <KpiTile
            label="Succeeded in Stripe"
            value={data.scanned_succeeded ?? 0}
            icon={CheckCircle2}
            tone="emerald"
            testid="kpi-succeeded"
          />
          <KpiTile
            label="Already paid in DB"
            value={data.already_paid_in_db ?? 0}
            icon={Database}
            tone="indigo"
            testid="kpi-paid-in-db"
          />
          <KpiTile
            label="Unreconciled"
            value={data.unreconciled_count ?? 0}
            icon={AlertTriangle}
            tone={data.unreconciled_count > 0 ? 'rose' : 'emerald'}
            testid="kpi-unreconciled"
          />
        </div>
      )}

      {data?.truncated && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="health-truncated-banner">
          ⚠️ Scan hit a safety limit (500 PIs or 22-second time budget). Narrow the window to see the rest.
        </div>
      )}

      <SectionPanel
        title="Unreconciled Stripe PaymentIntents"
        description="Stripe says succeeded — but no matching paid row in our DB. Use reconcile-by-pi to heal each."
        testid="unreconciled-list"
      >
        {!data?.items?.length ? (
          <EmptyState message="Nothing unreconciled. Healthy state." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">PI ID</th>
                  <th>Amount</th>
                  <th>Created</th>
                  <th>Customer</th>
                  <th>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {data.items.slice(0, 50).map((it) => (
                  <tr key={it.id} className="border-t" data-testid={`unreconciled-row-${it.id}`}>
                    <td className="py-2 font-mono text-xs">{it.id}</td>
                    <td>{cs()}{((it.amount || 0) / 100).toFixed(2)}</td>
                    <td className="text-xs text-slate-500">
                      {it.created ? new Date(it.created * 1000).toLocaleString() : '—'}
                    </td>
                    <td className="text-xs text-slate-600">{it.customer || '—'}</td>
                    <td className="text-xs">
                      {it.receipt_url ? (
                        <a
                          className="text-indigo-600 underline"
                          href={it.receipt_url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          view
                        </a>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionPanel>
    </div>
  );
};

// ---------------------------------------------------------------------------
// RECONCILIATION tab — nightly deep-heal audit trail
// ---------------------------------------------------------------------------

const ReconciliationTab = () => {
  const [days, setDays] = useState(7);
  const [outcome, setOutcome] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ days: String(days), limit: '200' });
      if (outcome) qs.set('outcome', outcome);
      const res = await axios.get(
        `${opsBase('/admin/transactions')}/reconciliation-audit?${qs.toString()}`,
        { headers: getAuthHeaders() },
      );
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [days, outcome]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runCount = Object.keys(data?.runs || {}).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
          <SelectTrigger className="w-32" data-testid="recon-window-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 day</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={outcome || 'all'} onValueChange={(v) => setOutcome(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="recon-outcome-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="matched">Matched</SelectItem>
            <SelectItem value="unchanged">Unchanged</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="not_found">Not found</SelectItem>
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchData}
          disabled={loading}
          data-testid="recon-refresh-btn"
        >
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <ErrorBanner err={error} onRetry={fetchData} testid="recon-error-banner" />

      <SectionPanel
        title="Recent runs"
        description="Each row is one nightly_deep_heal pass. Job runs at 3:30 AM."
        testid="recon-runs"
      >
        {runCount === 0 ? (
          <EmptyState message="No deep-heal runs in this window yet. First run is scheduled for 3:30 AM." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Run ID</th>
                  <th>Matched</th>
                  <th>Unchanged</th>
                  <th>Errors</th>
                  <th>Not found</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.runs).map(([rid, outcomes]) => (
                  <tr key={rid} className="border-t" data-testid={`recon-run-${rid}`}>
                    <td className="py-2 font-mono text-xs">{rid.slice(0, 8)}…</td>
                    <td><Badge variant="outline" className="bg-emerald-50 text-emerald-700">{outcomes.matched || 0}</Badge></td>
                    <td><Badge variant="outline">{outcomes.unchanged || 0}</Badge></td>
                    <td><Badge variant="outline" className="bg-rose-50 text-rose-700">{outcomes.error || 0}</Badge></td>
                    <td><Badge variant="outline" className="bg-amber-50 text-amber-700">{outcomes.not_found || 0}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionPanel>

      <SectionPanel
        title={`Audit rows (${data?.row_count || 0})`}
        description="Per-txn outcomes from every reconcile-by-pi call."
        testid="recon-rows"
      >
        {!data?.rows?.length ? (
          <EmptyState message="No audit rows match the current filter." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Time</th>
                  <th>Entity</th>
                  <th>PI</th>
                  <th>Outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.slice(0, 100).map((r) => (
                  <tr key={r.id} className="border-t">
                    <td className="py-2 text-xs text-slate-500">
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </td>
                    <td className="text-xs">
                      {r.entity_type || '—'}{r.entity_id ? ` · ${r.entity_id.slice(0, 8)}` : ''}
                    </td>
                    <td className="font-mono text-xs">{r.stripe_payment_intent_id?.slice(0, 14)}…</td>
                    <td>
                      <OutcomeBadge outcome={r.outcome} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionPanel>
    </div>
  );
};

const OutcomeBadge = ({ outcome }) => {
  const map = {
    matched: { tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    unchanged: { tone: 'bg-slate-50 text-slate-600 border-slate-200', icon: Clock },
    error: { tone: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle },
    not_found: { tone: 'bg-amber-50 text-amber-800 border-amber-200', icon: AlertTriangle },
  };
  const m = map[outcome] || map.unchanged;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium ${m.tone}`}>
      <Icon className="h-3 w-3" />
      {outcome || 'unknown'}
    </span>
  );
};

// ---------------------------------------------------------------------------
// COMPLIANCE tab — payment_method role alerts
// ---------------------------------------------------------------------------

const ComplianceTab = () => {
  const [status, setStatus] = useState('open');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${opsBase('/admin/transactions')}/compliance-alerts?status=${status}&limit=200`,
        { headers: getAuthHeaders() },
      );
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const acknowledge = async (alertId) => {
    try {
      await axios.post(
        `${opsBase('/admin/transactions')}/compliance-alerts/${alertId}/acknowledge`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success('Alert acknowledged');
      fetchData();
    } catch (e) {
      toast.error(`Ack failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <KpiTile
          label="Open alerts"
          value={data?.open_count ?? 0}
          icon={ShieldAlert}
          tone={data?.open_count > 0 ? 'rose' : 'emerald'}
          testid="kpi-compliance-open"
        />
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="compliance-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="acknowledged">Acknowledged</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            onClick={fetchData}
            disabled={loading}
            data-testid="compliance-refresh-btn"
          >
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <ErrorBanner err={error} onRetry={fetchData} testid="compliance-error-banner" />

      <SectionPanel
        title="Payment-method compliance alerts"
        description="Webhook detected ACH usage by a customer-role user. Defense-in-depth — these are NOT auto-refunded."
        testid="compliance-alerts"
      >
        {!data?.items?.length ? (
          <EmptyState message="No alerts in this state. Healthy." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Detected</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Entity</th>
                  <th>PM</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id} className="border-t" data-testid={`compliance-alert-${a.id}`}>
                    <td className="py-2 text-xs text-slate-500">
                      {a.detected_at ? new Date(a.detected_at).toLocaleString() : '—'}
                    </td>
                    <td className="text-xs">{a.user_email || a.user_id}</td>
                    <td>
                      <Badge variant="outline" className="bg-amber-50 text-amber-800">
                        {a.user_role}
                      </Badge>
                    </td>
                    <td className="text-xs">{a.entity_type} · {a.entity_id?.slice(0, 8)}</td>
                    <td className="font-mono text-xs">{a.payment_method_type}</td>
                    <td>{cs()}{a.amount?.toFixed(2)}</td>
                    <td>
                      {a.status === 'open' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => acknowledge(a.id)}
                          data-testid={`compliance-ack-${a.id}`}
                        >
                          Acknowledge
                        </Button>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50">
                          ack'd
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionPanel>
    </div>
  );
};

// ---------------------------------------------------------------------------
// WEBHOOKS tab — events + retry alerts
// ---------------------------------------------------------------------------

const WebhooksTab = () => {
  const [days, setDays] = useState(7);
  const [eventType, setEventType] = useState('');
  const [outcome, setOutcome] = useState('');
  const [data, setData] = useState(null);
  const [retryAlerts, setRetryAlerts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ days: String(days), limit: '200' });
      if (eventType) qs.set('event_type', eventType);
      if (outcome) qs.set('outcome', outcome);
      const [eventsRes, retryRes] = await Promise.all([
        axios.get(`${opsBase('/admin/transactions')}/webhook-events?${qs.toString()}`, { headers: getAuthHeaders() }),
        axios.get(`${opsBase('/admin/transactions')}/webhook-retry-alerts?status=open`, { headers: getAuthHeaders() }),
      ]);
      setData(eventsRes.data);
      setRetryAlerts(retryRes.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [days, eventType, outcome]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const eventTypes = Object.keys(data?.by_event_type || {});

  const ackRetry = async (alertId) => {
    try {
      await axios.post(
        `${opsBase('/admin/transactions')}/webhook-retry-alerts/${alertId}/acknowledge`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success('Retry alert acknowledged');
      fetchData();
    } catch (e) {
      toast.error(`Ack failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const successCount = Object.values(data?.by_event_type || {}).reduce(
    (sum, oc) => sum + (oc.success || 0), 0,
  );
  const errorCount = Object.values(data?.by_event_type || {}).reduce(
    (sum, oc) => sum + (oc.error || 0), 0,
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1">1 day</SelectItem>
            <SelectItem value="7">7 days</SelectItem>
            <SelectItem value="30">30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={eventType || 'all'} onValueChange={(v) => setEventType(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-64" data-testid="webhook-event-type-select"><SelectValue placeholder="All event types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All event types</SelectItem>
            {eventTypes.map((et) => (
              <SelectItem key={et} value={et}>{et}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={outcome || 'all'} onValueChange={(v) => setOutcome(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All outcomes</SelectItem>
            <SelectItem value="success">Success</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="in_flight">In flight</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={fetchData} disabled={loading} data-testid="webhook-refresh-btn">
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <ErrorBanner err={error} onRetry={fetchData} testid="webhook-error-banner" />

      <div className="grid gap-3 md:grid-cols-4">
        <KpiTile label="Total events" value={(data?.row_count) ?? 0} icon={Activity} testid="kpi-webhook-total" />
        <KpiTile label="Successes" value={successCount} icon={CheckCircle2} tone="emerald" testid="kpi-webhook-success" />
        <KpiTile label="Errors" value={errorCount} icon={XCircle} tone={errorCount > 0 ? 'rose' : 'slate'} testid="kpi-webhook-error" />
        <KpiTile
          label="Open retry alerts"
          value={retryAlerts?.open_count ?? 0}
          icon={AlertTriangle}
          tone={retryAlerts?.open_count > 0 ? 'rose' : 'emerald'}
          testid="kpi-webhook-retry"
        />
      </div>

      {retryAlerts?.items?.length > 0 && (
        <SectionPanel
          title="Webhook delivery retry alerts"
          description="Stripe redelivered these events ≥ 2 times — likely a handler threw on the first attempt."
          testid="webhook-retry-alerts"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Event ID</th>
                  <th>Type</th>
                  <th>Attempts</th>
                  <th>Last seen</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {retryAlerts.items.map((a) => (
                  <tr key={a.id} className="border-t" data-testid={`webhook-retry-row-${a.id}`}>
                    <td className="py-2 font-mono text-xs">{a.event_id?.slice(0, 16)}…</td>
                    <td className="text-xs">{a.event_type}</td>
                    <td><Badge variant="outline" className="bg-rose-50 text-rose-700">{a.delivery_attempts}</Badge></td>
                    <td className="text-xs text-slate-500">
                      {a.last_seen_at ? new Date(a.last_seen_at).toLocaleString() : '—'}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => ackRetry(a.id)}
                        data-testid={`webhook-retry-ack-${a.id}`}
                      >
                        Acknowledge
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionPanel>
      )}

      <SectionPanel
        title="Event delivery breakdown"
        description="Per-event-type counts. Hover a row for avg processing duration."
        testid="webhook-breakdown"
      >
        {!Object.keys(data?.by_event_type || {}).length ? (
          <EmptyState message="No webhook events in this window." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="py-2">Event type</th>
                  <th>Success</th>
                  <th>Error</th>
                  <th>In flight</th>
                  <th>Avg duration</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data.by_event_type).map(([et, oc]) => (
                  <tr key={et} className="border-t" data-testid={`webhook-event-type-${et}`}>
                    <td className="py-2 font-mono text-xs">{et}</td>
                    <td><Badge variant="outline" className="bg-emerald-50 text-emerald-700">{oc.success || 0}</Badge></td>
                    <td>
                      <Badge variant="outline" className={oc.error ? 'bg-rose-50 text-rose-700' : ''}>
                        {oc.error || 0}
                      </Badge>
                    </td>
                    <td><Badge variant="outline">{oc.in_flight || 0}</Badge></td>
                    <td className="text-xs text-slate-500">
                      {data.duration_summary?.[et]?.avg_ms != null
                        ? `${data.duration_summary[et].avg_ms} ms`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionPanel>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DATA HYGIENE tab — 3 admin migration triggers
// ---------------------------------------------------------------------------

const HygieneTab = () => {
  const [migrationResults, setMigrationResults] = useState({});
  const [running, setRunning] = useState(null);
  const [confirmFor, setConfirmFor] = useState(null);

  const callMigration = async (name, url, opts = {}) => {
    setRunning(name);
    try {
      const res = await axios.post(url, {}, { headers: getAuthHeaders() });
      setMigrationResults((m) => ({ ...m, [name]: res.data }));
      toast.success(
        opts.dryRun
          ? `Dry-run complete — ${res.data.applied || res.data.migrated || res.data.total_applied || 0} rows would change`
          : `Migration applied — ${res.data.applied || res.data.migrated || res.data.total_applied || 0} rows changed`,
      );
    } catch (e) {
      toast.error(`${name} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRunning(null);
    }
  };

  const migrations = [
    {
      key: 'normalize-session-id',
      title: 'Normalize stripe_session_id (N2)',
      description:
        'Copy legacy `session_id` → canonical `stripe_session_id` on payment_transactions. Idempotent.',
      dryUrl: `${opsBase('/admin/transactions')}/normalize-session-id?dry_run=true&limit=2000`,
      applyUrl: `${opsBase('/admin/transactions')}/normalize-session-id?dry_run=false&limit=2000`,
    },
    {
      key: 'backfill-credit-hold-ids',
      title: 'Backfill legacy credit hold IDs (iter306w)',
      description:
        'Stamp credit_hold_id on legacy pending lane bookings. NEVER touches customer_credits balance.',
      dryUrl: `${opsBase('/admin/transactions')}/backfill-credit-hold-ids?dry_run=true&limit=500`,
      applyUrl: `${opsBase('/admin/transactions')}/backfill-credit-hold-ids?dry_run=false&limit=500`,
    },
    {
      key: 'backfill-due-dates',
      title: 'Backfill missing due_dates (M2)',
      description:
        'For monthly_invoices, enrollments, manual_invoices, small_group_invoices missing a due_date, set it to created_at + 30 days. Lane bookings and tournaments are excluded.',
      dryUrl: `${opsBase('/admin/transactions')}/invoices/backfill-missing-due-dates?dry_run=true&grace_days=30&limit_per_collection=2000`,
      applyUrl: `${opsBase('/admin/transactions')}/invoices/backfill-missing-due-dates?dry_run=false&grace_days=30&limit_per_collection=2000`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
        <strong>Safety note:</strong> Every migration here defaults to dry-run on the first
        click. Review the result, then click "Apply for real" to commit. All migrations are
        idempotent — safe to re-run.
      </div>

      {migrations.map((m) => {
        const result = migrationResults[m.key];
        return (
          <SectionPanel
            key={m.key}
            title={m.title}
            description={m.description}
            testid={`migration-${m.key}`}
            action={
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => callMigration(m.key, m.dryUrl, { dryRun: true })}
                  disabled={running === m.key}
                  data-testid={`migration-dryrun-${m.key}`}
                >
                  {running === m.key ? 'Running…' : 'Dry run'}
                </Button>
                <Button
                  size="sm"
                  onClick={() => setConfirmFor(m)}
                  disabled={running === m.key}
                  data-testid={`migration-apply-${m.key}`}
                >
                  Apply for real
                </Button>
              </div>
            }
          >
            {result ? (
              <pre className="overflow-x-auto rounded bg-slate-50 px-3 py-2 text-xs">
                {JSON.stringify(result, null, 2)}
              </pre>
            ) : (
              <p className="text-xs text-slate-500">No run yet.</p>
            )}
          </SectionPanel>
        );
      })}

      <ConfirmDialog
        open={!!confirmFor}
        onOpenChange={(open) => { if (!open) setConfirmFor(null); }}
        title={`Apply: ${confirmFor?.title}?`}
        description="This will commit changes to the database. Migrations are idempotent, so re-running is safe."
        confirmText="Apply for real"
        onConfirm={() => {
          if (confirmFor) callMigration(confirmFor.key, confirmFor.applyUrl);
          setConfirmFor(null);
        }}
        onCancel={() => setConfirmFor(null)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------

export default function PaymentOperations({ tenantId }) {
  OPS_TENANT = tenantId || null;
  return (
    <div className="space-y-6 py-6" data-testid="payment-operations-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Payment Operations</h1>
            <p className="mt-1 text-sm text-slate-600">
              Unified admin console for the payment pipeline: Stripe reconciliation,
              compliance defense-in-depth, webhook delivery health, auto-pay readiness,
              and one-off data-hygiene migrations.
            </p>
          </div>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800">
            <ChevronRight className="mr-1 h-3 w-3" />
            iter306u-ad
          </Badge>
        </div>

        <Tabs defaultValue="health" className="space-y-4">
          <TabsList data-testid="payment-ops-tabs">
            <TabsTrigger value="health" data-testid="tab-health">
              <Activity className="mr-1.5 h-4 w-4" />
              Health
            </TabsTrigger>
            <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Reconciliation
            </TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">
              <ShieldAlert className="mr-1.5 h-4 w-4" />
              Compliance
            </TabsTrigger>
            <TabsTrigger value="webhooks" data-testid="tab-webhooks">
              <Database className="mr-1.5 h-4 w-4" />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="drift" data-testid="tab-drift">
              <AlertTriangle className="mr-1.5 h-4 w-4" />
              Data-Drift Findings
            </TabsTrigger>
            {/* iter392 — Data Hygiene one-off backfills retired (listed in the Endpoint Report). */}
            {RETIRED_TOOLS_VISIBLE && (
              <TabsTrigger value="hygiene" data-testid="tab-hygiene">
                <Wrench className="mr-1.5 h-4 w-4" />
                Data Hygiene
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="health"><HealthTab /></TabsContent>
          <TabsContent value="reconciliation"><ReconciliationTab /></TabsContent>
          <TabsContent value="compliance"><ComplianceTab /></TabsContent>
          <TabsContent value="webhooks"><WebhooksTab /></TabsContent>
          <TabsContent value="drift"><TenantDriftPanel tenantId={tenantId} /></TabsContent>
          {RETIRED_TOOLS_VISIBLE && <TabsContent value="hygiene"><HygieneTab /></TabsContent>}
        </Tabs>
    </div>
  );
}
